import { Box, Card, CardContent, CircularProgress, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { dateFormat } from '../../../shared/helpers/dateFormat';
import { appointmentService } from '../../../shared/services';
import { IPropsEmployeePage } from '../Employee';

const COLORS = ['#c2185b', '#f06292', '#880e4f', '#ad1457', '#e91e63', '#f48fb1', '#f8bbd0'];

export const EmployeeDashboard: React.FC<IPropsEmployeePage> = ({ business, selectedBizEmp }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Busca appointments via businessEmployeeId (mais específico) ou businessId
  const { data: appointments, isFetching } = useQuery({
    queryKey: ['appointments-employee-dashboard', business ? `biz-${business.id}` : `emp-${selectedBizEmp?.id}`],
    queryFn: () => {
      if (business) return appointmentService.get(business.id);
      if (selectedBizEmp?.id) return appointmentService.get(undefined, selectedBizEmp.id);
      return [];
    },
  });

  // Appointments by status (donut)
  const statusData = useMemo(() => {
    if (!appointments) return [];
    const completed = appointments.filter(a => a.completed && !a.canceled).length;
    const pending = appointments.filter(a => a.past && !a.completed && !a.canceled).length;
    const canceled = appointments.filter(a => a.canceled).length;
    const upcoming = appointments.filter(a => !a.past && !a.canceled).length;
    return [
      { name: 'Concluídos', value: completed },
      { name: 'Pendentes', value: pending },
      { name: 'Cancelados', value: canceled },
      { name: 'Futuros', value: upcoming },
    ].filter(d => d.value > 0);
  }, [appointments]);

  // Revenue by day for current month (bar)
  const revenueByDay = useMemo(() => {
    if (!appointments) return [];
    const now = new Date();
    const completed = appointments.filter(a => {
      const d = new Date(a.date);
      return a.completed && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    const byDay: Record<string, number> = {};
    completed.forEach(a => {
      const key = dateFormat(a.date, 'DD/MM');
      byDay[key] = (byDay[key] || 0) + (a.totalAmount || 0);
    });
    return Object.entries(byDay).map(([day, value]) => ({ day, value }));
  }, [appointments]);

  // Top services (pie)
  const topServices = useMemo(() => {
    if (!appointments) return [];
    const counts: Record<string, number> = {};
    appointments.filter(a => a.completed).forEach(a => {
      a.services?.forEach(s => {
        counts[s.description] = (counts[s.description] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);
  }, [appointments]);

  // Revenue trend (area chart) — last 30 days
  const revenueTrend = useMemo(() => {
    if (!appointments) return [];
    const completed = appointments.filter(a => a.completed);
    const byDay: Record<string, number> = {};
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      byDay[dateFormat(d, 'DD/MM')] = 0;
    }
    completed.forEach(a => {
      const key = dateFormat(a.date, 'DD/MM');
      if (key in byDay) byDay[key] += a.totalAmount || 0;
    });
    return Object.entries(byDay).map(([day, value]) => ({ day, value }));
  }, [appointments]);

  const cardSx = {
    borderRadius: '16px',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 'none',
    height: '100%',
  };

  const axisStyle = { fill: theme.palette.text.secondary, fontSize: 11 };
  const gridColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const primaryColor = theme.palette.primary.main;

  if (isFetching) {
    return <Box display='flex' justifyContent='center' py={8}><CircularProgress /></Box>;
  }

  const totalRevenue = appointments?.filter(a => a.completed).reduce((t, a) => t + (a.totalAmount || 0), 0) || 0;
  const totalCompleted = appointments?.filter(a => a.completed).length || 0;
  const totalAppointments = appointments?.length || 0;

  return (
    <Box display='flex' flexDirection='column' gap={3}>
      {/* KPI chips row */}
      <Box display='flex' gap={2} flexWrap='wrap'>
        {[
          { label: 'Receita total', value: `R$ ${totalRevenue.toFixed(2)}`, color: 'success.main' },
          { label: 'Agend. concluídos', value: totalCompleted, color: 'primary.main' },
          { label: 'Agend. totais', value: totalAppointments, color: 'text.primary' },
          { label: 'Ticket médio', value: totalCompleted > 0 ? `R$ ${(totalRevenue / totalCompleted).toFixed(2)}` : 'R$ 0,00', color: 'warning.main' },
        ].map(kpi => (
          <Card key={kpi.label} sx={{ ...cardSx, minWidth: 160, flex: 1 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant='caption' color='text.secondary'>{kpi.label}</Typography>
              <Typography variant='h6' fontWeight={700} color={kpi.color}>{kpi.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Revenue trend — 30 days */}
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant='subtitle1' fontWeight={700} mb={2}>Receita — últimos 30 dias</Typography>
          <ResponsiveContainer width='100%' height={220}>
            <AreaChart data={revenueTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id='colorRevenueEmp' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor={primaryColor} stopOpacity={0.3} />
                  <stop offset='95%' stopColor={primaryColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray='3 3' stroke={gridColor} />
              <XAxis dataKey='day' tick={axisStyle} interval='preserveStartEnd' />
              <YAxis tick={axisStyle} tickFormatter={(v: number) => `R$${v}`} />
              <Tooltip formatter={(v: unknown) => [`R$ ${Number(v).toFixed(2)}`, 'Receita']} />
              <Area type='monotone' dataKey='value' stroke={primaryColor} strokeWidth={2} fill='url(#colorRevenueEmp)' />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Revenue by day (current month) + Status donut */}
      <Box display='flex' gap={2} flexWrap='wrap'>
        <Card sx={{ ...cardSx, flex: 2, minWidth: 260 }}>
          <CardContent>
            <Typography variant='subtitle1' fontWeight={700} mb={2}>Receita por dia (mês atual)</Typography>
            <ResponsiveContainer width='100%' height={220}>
              <BarChart data={revenueByDay} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray='3 3' stroke={gridColor} />
                <XAxis dataKey='day' tick={axisStyle} />
                <YAxis tick={axisStyle} tickFormatter={(v: number) => `R$${v}`} />
                <Tooltip formatter={(v: unknown) => [`R$ ${Number(v).toFixed(2)}`, 'Receita']} />
                <Bar dataKey='value' fill={primaryColor} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card sx={{ ...cardSx, flex: 1, minWidth: 220 }}>
          <CardContent>
            <Typography variant='subtitle1' fontWeight={700} mb={2}>Status dos agendamentos</Typography>
            <ResponsiveContainer width='100%' height={220}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx='50%'
                  cy='45%'
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey='value'
                >
                  {statusData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Top services */}
      <Card sx={cardSx}>
        <CardContent>
          <Typography variant='subtitle1' fontWeight={700} mb={2}>Serviços mais realizados</Typography>
          {topServices.length === 0 ? (
            <Box textAlign='center' py={4}>
              <Typography color='text.secondary' variant='body2'>Sem dados ainda.</Typography>
            </Box>
          ) : (
            <ResponsiveContainer width='100%' height={220}>
              <PieChart>
                <Pie
                  data={topServices}
                  cx='50%'
                  cy='45%'
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey='value'
                  label={({ percent }) => `${((percent ?? 0) * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {topServices.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
